import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { buildApiUrl, buildMediaUrl } from '../../config/api';
import { fetchJson, formatFetchError } from '../../utils/fetchJson';
import { getProfilePhotoUrl } from '../../utils/profileUtils';
import Picker, { Emoji } from 'emoji-picker-react';
import {
  FiMoreVertical, FiSmile, FiSend, FiImage, FiSlash, FiTrash2,
  FiAlertCircle, FiX, FiMessageSquare, FiCheck, FiCheckCircle, FiClock
} from 'react-icons/fi';
import { HiOutlineSparkles } from 'react-icons/hi2';
import { useSocket } from '../../context/SocketContext';
import { useToast } from '../../context/ToastContext';

// ─── Request status values returned by /api/messages/requests/status/:userId ──
// 'none'                — no request, either side can send
// 'pending' + 'sent'    — current user sent, waiting
// 'pending' + 'received'— current user received a pending request
// 'accepted'            — request accepted, full chat unlocked
// 'declined' + 'sent'   — current user's request was declined (can retry)
// 'conversation_active' — existing messages already exchanged

const MessageModal = ({ isOpen, onClose, recipient, currentUser, onSendMessage }) => {
  const { socket, onlineUsers, joinConversation, leaveConversation, startTyping, stopTyping } = useSocket();
  const { success, warning, error: showError } = useToast();

  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBlocking, setIsBlocking] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [typingUser, setTypingUser] = useState(null);

  // Message Request state
  const [requestStatus, setRequestStatus] = useState(null);   // null = loading
  const [requestDirection, setRequestDirection] = useState(null); // 'sent' | 'received'
  const [isSendingRequest, setIsSendingRequest] = useState(false);
  const [isRespondingRequest, setIsRespondingRequest] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // ── Emoji rendering ────────────────────────────────────────
  const emojiRegex = /(\p{Extended_Pictographic}(?:\uFE0F|\u200D\p{Extended_Pictographic})*)/gu;
  const isEmojiSeg = (s) => { if (!s) return false; emojiRegex.lastIndex = 0; return emojiRegex.test(s); };
  const toUnified = (s) => s.split('\u200d').map(p => [...p].map(c => c.codePointAt(0).toString(16)).join('-')).join('-200d-');
  const renderEmojiText = (text) => {
    if (!text || typeof text !== 'string') return null;
    return text.split(emojiRegex).filter(Boolean).map((part, i) =>
      isEmojiSeg(part)
        ? <Emoji key={i} unified={toUnified(part)} size={22} emojiStyle="facebook" />
        : <span key={i}>{part}</span>
    );
  };

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const getRecipientId = useCallback(() => recipient?.userId || recipient?._id || recipient?.id, [recipient]);
  const resolvedUserId = currentUser?._id || currentUser?.id;
  const targetRecipientId = getRecipientId();

  const activeConversationRoomId = useMemo(() => {
    if (!resolvedUserId || !targetRecipientId) return null;
    const [a, b] = [String(resolvedUserId), String(targetRecipientId)].sort();
    return `conv_${a}_${b}`;
  }, [resolvedUserId, targetRecipientId]);

  const isImageMsg = (msg) => msg && (msg.messageType === 'image' || msg.type === 'image' || Boolean(msg.image) || Boolean(msg.imageUrl));

  const getImgUrl = (path) => {
    if (!path) return null;
    if (typeof path === 'string' && (path.startsWith('http://') || path.startsWith('https://'))) return path;
    return buildMediaUrl(path);
  };

  const recipientOnline = targetRecipientId ? onlineUsers.has(String(targetRecipientId)) : false;

  // ── Fetch request status ────────────────────────────────────
  const fetchRequestStatus = useCallback(async () => {
    if (!targetRecipientId) return;
    try {
      const token = localStorage.getItem('token');
      const { response, data } = await fetchJson(
        buildApiUrl(`/api/messages/requests/status/${targetRecipientId}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok && data.success) {
        setRequestStatus(data.data.status);
        setRequestDirection(data.data.direction || null);
      }
    } catch {
      setRequestStatus('none');
    }
  }, [targetRecipientId]);

  const fetchMessages = useCallback(async () => {
    const id = getRecipientId();
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const { response, data } = await fetchJson(buildApiUrl(`/api/messages/${id}`), {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      setMessages(response.ok && data.success ? data.data.messages || [] : []);
    } catch (err) {
      setError(formatFetchError(err, 'Failed to load messages'));
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [getRecipientId]);

  useEffect(() => {
    if (isOpen && recipient) {
      fetchRequestStatus();
      fetchMessages();
      setTimeout(() => inputRef.current?.focus(), 100);
      if (recipient.blocked || recipient.isBlocked) setIsBlocked(true);
    }
  }, [isOpen, recipient, fetchMessages, fetchRequestStatus]);

  useEffect(() => {
    if (activeConversationRoomId && socket && isOpen) {
      joinConversation(activeConversationRoomId);
      return () => leaveConversation(activeConversationRoomId);
    }
  }, [activeConversationRoomId, socket, joinConversation, leaveConversation, isOpen]);

  // Socket event handlers
  useEffect(() => {
    if (!isOpen) return;

    const onMsg = (e) => {
      const msg = e.detail;
      if (String(msg?.senderId || msg?.from) === String(targetRecipientId)) {
        const image = msg.imageUrl || msg.image;
        const isImg = isImageMsg(msg);
        setMessages(prev => [...prev, {
          id: msg.id || msg._id || Date.now().toString(),
          message: msg.text || msg.message || '',
          sentAt: msg.timestamp || new Date().toISOString(),
          sender: recipient,
          type: isImg ? 'image' : 'text',
          image: isImg ? image : null,
          imageUrl: isImg ? getImgUrl(image) : null
        }]);
      }
    };

    const onTyping = (e) => {
      const { conversationId, userName, isTyping } = e.detail || {};
      if (activeConversationRoomId && String(conversationId) === String(activeConversationRoomId))
        setTypingUser(isTyping ? userName : null);
    };

    const onConvDeleted = (e) => {
      if (String(e.detail.partnerId) === String(targetRecipientId)) {
        setMessages([]);
        if (isOpen) warning('This conversation was deleted from another device.');
      }
    };

    // Sender learns their request was accepted/declined
    const onRequestResponded = (e) => {
      const { action, recipientName } = e.detail;
      if (action === 'accepted') {
        setRequestStatus('accepted');
        success(`🎉 ${recipientName || 'They'} accepted your message request! You can now chat freely.`);
        fetchMessages();
      } else if (action === 'declined') {
        setRequestStatus('declined');
        setRequestDirection('sent');
      }
    };

    window.addEventListener('socket_message', onMsg);
    window.addEventListener('socket_typing', onTyping);
    window.addEventListener('socket_conversation_deleted', onConvDeleted);
    window.addEventListener('socket_message_request_responded', onRequestResponded);

    return () => {
      window.removeEventListener('socket_message', onMsg);
      window.removeEventListener('socket_typing', onTyping);
      window.removeEventListener('socket_conversation_deleted', onConvDeleted);
      window.removeEventListener('socket_message_request_responded', onRequestResponded);
    };
  }, [isOpen, targetRecipientId, activeConversationRoomId, recipient, warning, success, fetchMessages]);

  useEffect(() => {
    const handle = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  // ── Send Message Request ────────────────────────────────────
  const handleSendMessageRequest = async () => {
    if (!targetRecipientId) return;
    setIsSendingRequest(true);
    try {
      const token = localStorage.getItem('token');
      const { response, data } = await fetchJson(buildApiUrl('/api/messages/requests/send'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipientId: targetRecipientId })
      });
      if (response.ok && data.success) {
        setRequestStatus('pending');
        setRequestDirection('sent');
        success("💌 Your message request has been sent! You'll be notified when they respond.");
      } else {
        showError(data?.message || 'Failed to send message request.');
      }
    } catch {
      showError('Something went wrong. Please try again.');
    } finally {
      setIsSendingRequest(false);
    }
  };

  // ── Accept / Decline ────────────────────────────────────────
  const handleRespondToRequest = async (action) => {
    if (!targetRecipientId) return;
    setIsRespondingRequest(true);
    try {
      const token = localStorage.getItem('token');
      const { response, data } = await fetchJson(buildApiUrl('/api/messages/requests/respond'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ senderId: targetRecipientId, action })
      });
      if (response.ok && data.success) {
        if (action === 'accept') {
          setRequestStatus('accepted');
          success("✅ You've accepted the message request. You can now chat with each other!");
          fetchMessages();
        } else {
          setRequestStatus('declined');
          setRequestDirection('received'); // will resolve to 'none' on next fetch so receiver can send
          warning('You have declined the message request.');
        }
      } else {
        showError(data?.message || 'Failed to process request.');
      }
    } catch {
      showError('Something went wrong. Please try again.');
    } finally {
      setIsRespondingRequest(false);
    }
  };

  // ── Send Message ────────────────────────────────────────────
  const handleSendMessage = async (e) => {
    if (e) e.preventDefault();
    if (!message.trim() || !targetRecipientId) return;
    const messageText = message.trim();
    const tempId = `temp-${Date.now()}`;
    const sentAt = new Date().toISOString();
    setMessages(prev => [...prev, { id: tempId, message: messageText, sender: currentUser, recipient, sentAt, isRead: false, temp: true, status: 'sent', type: 'text' }]);
    setMessage('');
    setShowEmojiPicker(false);
    try {
      const token = localStorage.getItem('token');
      const { response, data } = await fetchJson(buildApiUrl('/api/messages/send'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipientId: targetRecipientId, message: messageText })
      });
      if (response.ok && data.success) {
        const backendMsg = data.data.message;
        setMessages(prev => prev.map(m => m.id === tempId ? { ...backendMsg, id: backendMsg._id || backendMsg.id } : m));
        if (socket && activeConversationRoomId) {
          socket.emit('send_message', {
            conversationId: activeConversationRoomId,
            message: { text: messageText, messageType: 'text', senderName: currentUser?.firstName || 'Someone', senderPhoto: currentUser?.profilePhoto },
            senderId: resolvedUserId, receiverId: targetRecipientId
          });
        }
        if (onSendMessage) onSendMessage(data.data.message);
      } else {
        throw new Error(data?.message || 'Failed to send message');
      }
    } catch (err) {
      setMessages(prev => prev.filter(m => m.id !== tempId));
      setError(formatFetchError(err, 'Failed to send message'));
      setMessage(messageText);
    }
  };

  // ── Image Upload ────────────────────────────────────────────
  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !targetRecipientId) return;
    if (file.size > 15 * 1024 * 1024) { showError('Image must be smaller than 15MB'); return; }
    const tempId = `temp-img-${Date.now()}`;
    const previewUrl = URL.createObjectURL(file);
    setMessages(prev => [...prev, { id: tempId, message: '', sender: currentUser, recipient, sentAt: new Date().toISOString(), type: 'image', imageUrl: previewUrl, temp: true, status: 'sent' }]);
    setUploadingImage(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('image', file);
      formData.append('recipientId', targetRecipientId);
      const res = await fetch(buildApiUrl('/api/messages/send-image'), { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData });
      const data = await res.json();
      if (res.ok && data.success) {
        const bm = data.data.message;
        const finalUrl = getImgUrl(bm.image || bm.imageUrl) || previewUrl;
        setMessages(prev => prev.map(m => m.id === tempId ? { ...bm, id: bm._id || bm.id, type: 'image', imageUrl: finalUrl } : m));
        if (socket && activeConversationRoomId) {
          socket.emit('send_message', { conversationId: activeConversationRoomId, message: { text: '', messageType: 'image', imageUrl: bm.image || bm.imageUrl, senderName: currentUser?.firstName || 'Someone' }, senderId: resolvedUserId, receiverId: targetRecipientId });
        }
      } else throw new Error(data.message || 'Failed to send image');
    } catch (err) {
      setMessages(prev => prev.filter(m => m.id !== tempId));
      showError(err.message || 'Failed to send image');
    } finally {
      setUploadingImage(false);
      URL.revokeObjectURL(previewUrl);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteConversation = async () => {
    if (!targetRecipientId) return;
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const { response, data } = await fetchJson(buildApiUrl(`/api/messages/conversations/${targetRecipientId}`), { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (response.ok && data.success) {
        setMessages([]); success('Conversation deleted successfully'); setShowDropdown(false);
        window.dispatchEvent(new CustomEvent('conversation_deleted', { detail: { userId: targetRecipientId } }));
      } else throw new Error(data.message || 'Failed to delete conversation');
    } catch (err) { showError(err.message || 'Failed to delete conversation');
    } finally { setIsDeleting(false); }
  };

  const handleBlockUser = async () => {
    if (!targetRecipientId) return;
    setIsBlocking(true);
    try {
      const token = localStorage.getItem('token');
      const { response, data } = await fetchJson(buildApiUrl('/api/messages/block'), { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ userId: targetRecipientId }) });
      if (response.ok && data.success) { setIsBlocked(true); success('User blocked successfully'); setShowDropdown(false); }
      else throw new Error(data.message || 'Failed to block user');
    } catch (err) { showError(err.message || 'Failed to block user');
    } finally { setIsBlocking(false); }
  };

  const handleUnblockUser = async () => {
    if (!targetRecipientId) return;
    setIsBlocking(true);
    try {
      const token = localStorage.getItem('token');
      const { response, data } = await fetchJson(buildApiUrl('/api/messages/unblock'), { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ userId: targetRecipientId }) });
      if (response.ok && data.success) { setIsBlocked(false); success('User unblocked successfully'); setShowDropdown(false); }
      else throw new Error(data.message);
    } catch (err) { showError(err.message || 'Failed to unblock user');
    } finally { setIsBlocking(false); }
  };

  const handleInputChange = (e) => {
    setMessage(e.target.value);
    if (!isTyping && activeConversationRoomId) { setIsTyping(true); startTyping(activeConversationRoomId, currentUser?.firstName || 'Someone'); }
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => { setIsTyping(false); if (activeConversationRoomId) stopTyping(activeConversationRoomId); }, 1500);
  };

  const fmtTime = (date) => {
    if (!date) return 'Just now';
    const d = new Date(date), now = new Date(), diffH = Math.floor((now - d) / 3600000);
    if (diffH < 1) { const m = Math.floor((now - d) / 60000); return m < 1 ? 'Just now' : `${m} min ago`; }
    if (diffH < 24) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diffH < 48) return 'Yesterday';
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const chatEnabled = requestStatus === 'accepted' || requestStatus === 'conversation_active';

  if (!isOpen) return null;

  // ─── Render ────────────────────────────────────────────────
  const renderRequestArea = () => {
    if (loading || requestStatus === null) {
      return (
        <div className="flex flex-col items-center justify-center h-full space-y-3">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-romantic-500"></div>
          <p className="text-gray-500 text-sm">Loading conversation...</p>
        </div>
      );
    }

    // ── No request / receiver-who-declined: show Send Request panel ──
    if (requestStatus === 'none') {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center p-6">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mb-4"
            style={{ background: 'linear-gradient(135deg, #f43f5e 0%, #ec4899 100%)' }}>
            <FiMessageSquare size={34} className="text-white" />
          </div>
          <h4 className="text-xl font-bold text-gray-900 mb-2">Send a Message Request</h4>
          <p className="text-gray-500 text-sm max-w-xs mb-1 leading-relaxed">
            To begin a conversation with <strong className="text-gray-700">{recipient.firstName}</strong>, send them a message request first.
            They will be notified and can choose to accept or decline.
          </p>
          <p className="text-xs text-gray-400 mb-6">Once accepted, you'll both be able to chat freely. 💬</p>
          <button
            onClick={handleSendMessageRequest}
            disabled={isSendingRequest}
            id="send-message-request-btn"
            className="flex items-center justify-center gap-2 px-8 py-3 rounded-full font-semibold text-white shadow-lg transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100"
            style={{ background: 'linear-gradient(135deg, #f43f5e 0%, #ec4899 100%)', boxShadow: '0 8px 24px -4px rgba(244,63,94,0.4)' }}
          >
            {isSendingRequest
              ? <><div className="w-4 h-4 border-2 border-white border-t-transparent animate-spin rounded-full"></div><span>Sending Request…</span></>
              : <><HiOutlineSparkles size={18} /><span>Send Message Request</span></>}
          </button>
        </div>
      );
    }

    // ── Sender waiting for response ──
    if (requestStatus === 'pending' && requestDirection === 'sent') {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center p-6">
          <div className="w-20 h-20 bg-amber-50 border-4 border-amber-200 rounded-full flex items-center justify-center mb-4">
            <FiClock size={32} className="text-amber-500" />
          </div>
          <h4 className="text-xl font-bold text-gray-900 mb-2">Request Sent!</h4>
          <p className="text-gray-500 text-sm max-w-xs leading-relaxed mb-1">
            Your message request has been sent to <strong className="text-gray-700">{recipient.firstName}</strong>.
          </p>
          <p className="text-gray-400 text-xs leading-relaxed">You'll receive a notification as soon as they respond. Sit tight! 🤞</p>
          <div className="flex items-center gap-1.5 mt-6">
            {[0, 1, 2].map(i => (
              <div key={i} className="w-2.5 h-2.5 rounded-full bg-amber-400"
                style={{ animation: `msgBounce 1.2s ease-in-out ${i * 0.2}s infinite` }} />
            ))}
          </div>
          <p className="text-[11px] text-amber-600 font-medium mt-2">Waiting for response…</p>
        </div>
      );
    }

    // ── Receiver sees an incoming pending request ──
    if (requestStatus === 'pending' && requestDirection === 'received') {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center p-6">
          <div className="w-20 h-20 rounded-full ring-4 ring-rose-100 overflow-hidden mb-4">
            <img src={getProfilePhotoUrl(recipient)} alt="" className="w-full h-full object-cover" />
          </div>
          <h4 className="text-xl font-bold text-gray-900 mb-1">Message Request</h4>
          <p className="text-gray-600 text-sm mb-1">
            <strong>{recipient.firstName} {recipient.lastName}</strong> has sent you a message request.
          </p>
          <p className="text-gray-400 text-xs max-w-xs leading-relaxed mb-6">
            Accept to start chatting, or decline if you're not interested. Your decision is private.
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleRespondToRequest('decline')}
              disabled={isRespondingRequest}
              className="px-6 py-2.5 rounded-full border-2 border-gray-300 text-gray-600 font-semibold text-sm hover:bg-gray-100 transition-all disabled:opacity-50"
            >
              {isRespondingRequest
                ? <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent animate-spin rounded-full mx-auto"></div>
                : 'Decline'}
            </button>
            <button
              onClick={() => handleRespondToRequest('accept')}
              disabled={isRespondingRequest}
              id="accept-message-request-btn"
              className="px-8 py-2.5 rounded-full font-semibold text-sm text-white shadow-md transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:scale-100 flex items-center gap-2"
              style={{ background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' }}
            >
              {isRespondingRequest
                ? <div className="w-4 h-4 border-2 border-white border-t-transparent animate-spin rounded-full"></div>
                : <><FiCheck size={16} />Accept</>}
            </button>
          </div>
        </div>
      );
    }

    // ── Original sender's request was declined — offer to try again ──
    if (requestStatus === 'declined') {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center p-6">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <FiX size={28} className="text-gray-400" />
          </div>
          <h4 className="text-lg font-semibold text-gray-700 mb-2">Request Not Accepted</h4>
          <p className="text-gray-400 text-sm max-w-xs mb-5">
            {recipient.firstName} chose not to accept your message request at this time. You can try reaching out again if you'd like.
          </p>
          <button
            onClick={handleSendMessageRequest}
            disabled={isSendingRequest}
            className="flex items-center justify-center gap-2 px-7 py-2.5 rounded-full font-semibold text-sm text-white shadow-md transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:scale-100"
            style={{ background: 'linear-gradient(135deg, #f43f5e 0%, #ec4899 100%)' }}
          >
            {isSendingRequest
              ? <><div className="w-4 h-4 border-2 border-white border-t-transparent animate-spin rounded-full"></div><span>Sending…</span></>
              : <><HiOutlineSparkles size={16} /><span>Try Again</span></>}
          </button>
        </div>
      );
    }

    // ── Accepted / conversation_active — show chat messages ──
    if (messages.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
          <div className="w-16 h-16 bg-romantic-50 rounded-full flex items-center justify-center mb-4">
            <FiCheckCircle size={32} className="text-green-500" />
          </div>
          <h4 className="text-lg font-semibold text-gray-900 mb-2">
            {requestStatus === 'accepted' ? 'Request Accepted! Say Hello 👋' : 'Start the Conversation!'}
          </h4>
          <p className="text-gray-500 text-sm max-w-xs">
            {requestStatus === 'accepted'
              ? `${recipient.firstName} accepted your request. Send your first message to begin your journey together.`
              : `Send a message to ${recipient.firstName} and start your journey together.`}
          </p>
        </div>
      );
    }

    return (
      <div className="flex flex-col space-y-4 mt-auto">
        {messages.map((msg, i) => {
          const isMe = msg.from === currentUser?.id || msg.sender?._id === currentUser?.id || msg.sender?.id === currentUser?.id;
          const isImg = isImageMsg(msg);
          const imgUrl = isImg ? (msg.imageUrl || getImgUrl(msg.image)) : null;
          return (
            <div key={msg.id || msg._id || i} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
              {!isMe && <img src={getProfilePhotoUrl(recipient)} alt="" className="w-8 h-8 rounded-full object-cover mr-2 self-end shadow-sm" />}
              <div className={`max-w-[80%] flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                <div className={`px-4 py-2.5 rounded-2xl shadow-sm ${isMe ? 'bg-gradient-to-r from-romantic-500 to-primary-600 text-white rounded-br-none' : 'bg-white border border-gray-100 text-gray-900 rounded-bl-none'}`}>
                  {isImg && imgUrl
                    ? <div className="space-y-2"><img src={imgUrl} alt="Shared" className="rounded-lg max-h-64 w-full object-cover cursor-pointer hover:opacity-95" onClick={() => window.open(imgUrl, '_blank')} />{msg.message && <p className="text-sm break-words leading-relaxed">{renderEmojiText(msg.message)}</p>}</div>
                    : <p className="text-sm break-words leading-relaxed">{renderEmojiText(msg.message)}</p>}
                  <div className={`flex items-center justify-end space-x-1 mt-1.5 ${isMe ? 'text-romantic-100' : 'text-gray-400'}`}>
                    <span className="text-[10px] uppercase font-medium">{fmtTime(msg.sentAt)}</span>
                    {isMe && <span className="text-xs ml-1">✓</span>}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white rounded-t-2xl">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <img src={getProfilePhotoUrl(recipient)} alt="" className="w-11 h-11 rounded-full object-cover border-2 border-romantic-50 shadow-sm" />
                {recipientOnline && <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full shadow-sm"></span>}
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 leading-tight">{recipient.firstName} {recipient.lastName}</h3>
                <p className={`text-xs font-medium ${recipientOnline ? 'text-green-600' : 'text-gray-400'}`}>{recipientOnline ? 'Online now' : 'Offline'}</p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <div className="relative" ref={dropdownRef}>
                <button onClick={() => setShowDropdown(!showDropdown)} className="p-2 text-gray-400 hover:text-romantic-600 hover:bg-romantic-50 rounded-full transition-all">
                  <FiMoreVertical size={20} />
                </button>
                {showDropdown && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-[60]">
                    <button onClick={handleDeleteConversation} disabled={isDeleting} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 flex items-center space-x-3 transition-colors">
                      {isDeleting ? <div className="w-4 h-4 border-2 border-red-500 border-t-transparent animate-spin rounded-full"></div> : <FiTrash2 size={16} />}
                      <span>Delete Conversation</span>
                    </button>
                    <button onClick={isBlocked ? handleUnblockUser : handleBlockUser} disabled={isBlocking} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600 flex items-center space-x-3 transition-colors">
                      {isBlocking ? <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent animate-spin rounded-full"></div> : <FiSlash size={16} />}
                      <span>{isBlocked ? 'Unblock User' : 'Block User'}</span>
                    </button>
                    <button className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 flex items-center space-x-3 transition-colors">
                      <FiAlertCircle size={16} /><span>Report Misbehavior</span>
                    </button>
                  </div>
                )}
              </div>
              <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all">
                <FiX size={20} />
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 min-h-[400px] max-h-[500px] bg-gray-50 bg-opacity-50 flex flex-col">
            {renderRequestArea()}

            {uploadingImage && (
              <div className="flex justify-end mt-2">
                <div className="bg-white border border-gray-100 rounded-2xl rounded-tr-none px-4 py-2 shadow-sm flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-romantic-500"></div>
                  <span className="text-gray-500 text-xs">Sending photo...</span>
                </div>
              </div>
            )}
            {typingUser && (
              <div className="flex justify-start mt-2">
                <div className="bg-gray-100 rounded-2xl rounded-bl-none px-4 py-2 text-xs text-gray-500 italic">
                  {typingUser} is typing...
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Error */}
          {error && (
            <div className="mx-6 mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Input — only when chat is active */}
          {chatEnabled && (
            <div className="border-t border-gray-100 p-4 bg-white rounded-b-2xl relative">
              {showEmojiPicker && (
                <div className="absolute bottom-[calc(100%+10px)] left-4 z-50">
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-2xl overflow-hidden">
                    <Picker onEmojiClick={(e) => { setMessage(p => p + e.emoji); setShowEmojiPicker(false); }} theme="light" emojiStyle="facebook" lazyLoadEmojis width={320} height={400} />
                  </div>
                </div>
              )}
              <form onSubmit={handleSendMessage} className="flex items-center space-x-2">
                <button type="button" onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className={`p-2.5 rounded-full transition-all ${showEmojiPicker ? 'text-romantic-600 bg-romantic-50' : 'text-gray-400 hover:text-romantic-500 hover:bg-gray-100'}`}>
                  <FiSmile size={22} />
                </button>
                <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploadingImage}
                  className="p-2.5 text-gray-400 hover:text-romantic-500 hover:bg-gray-100 rounded-full transition-all">
                  <FiImage size={22} />
                </button>
                <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" className="hidden" />
                <div className="relative flex-1">
                  <input ref={inputRef} type="text" value={message} onChange={handleInputChange}
                    placeholder={`Message ${recipient.firstName}...`}
                    className="w-full pl-4 pr-12 py-2.5 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-romantic-500 transition-all text-sm placeholder:text-gray-400"
                    disabled={isBlocked || isDeleting} />
                  <button type="submit" disabled={!message.trim() || isBlocked || isDeleting}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 p-2 text-white bg-gradient-to-r from-romantic-500 to-primary-600 rounded-xl hover:shadow-lg hover:scale-105 active:scale-95 transition-all disabled:opacity-30 disabled:grayscale disabled:scale-100 disabled:cursor-not-allowed shadow-md"
                    aria-label="Send message">
                    <FiSend size={18} />
                  </button>
                </div>
              </form>
              {isBlocked && (
                <div className="mt-2 text-center text-[10px] text-orange-600 font-semibold bg-orange-50 py-1 rounded-lg">
                  You have blocked this user. Unblock to send messages.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <style>{`
        @keyframes msgBounce {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.5; }
          40% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default MessageModal;
